import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Paper,
    Alert,
    CircularProgress,
    Typography
} from '@mui/material';
import { getPendingApprovals, approveTimecard } from '../../services/timecardService';
import { format } from 'date-fns';
import HoursApprovalDialog from './HoursApprovalDialog';
import WeekCard from './WeekCard';

// Global singleton to prevent multiple polling instances
let globalPollingInterval = null;
let globalPollingActive = false;


const HoursApproval = () => {
    const [timecards, setTimecards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTimecard, setSelectedTimecard] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        direction: 'asc',
        field: 'user'
    });
    const [isOffline, setIsOffline] = useState(false);
    const [consecutiveErrors, setConsecutiveErrors] = useState(0);
    const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState(Date.now());
    const [hiddenWeeks, setHiddenWeeks] = useState(new Set());
    
    // Use refs to avoid useCallback dependencies
    const consecutiveErrorsRef = useRef(0);
    const lastSuccessfulFetchRef = useRef(Date.now());
    const intervalRef = useRef(null);

    // Helper function to parse UTC date string
    const parseUTCDate = (dateString) => {
        const date = new Date(dateString);
        return new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            0, 0, 0, 0
        );
    };

    const fetchTimecards = useCallback(async () => {
        // Circuit breaker: if we've had too many consecutive errors, reduce polling frequency
        const now = Date.now();
        const timeSinceLastSuccess = now - lastSuccessfulFetchRef.current;
        
        if (consecutiveErrorsRef.current >= 2) {
            console.log(`Circuit breaker: Skipping fetch due to ${consecutiveErrorsRef.current} consecutive errors. Last success: ${Math.floor(timeSinceLastSuccess / 1000)}s ago`);
            return;
        }

        try {
            setLoading(true);
            const data = await getPendingApprovals();
            console.log('Fetched timecards:', data.map(tc => ({
                id: tc._id,
                user: tc.user.name,
                project: tc.project,
                status: tc.status,
                days: tc.days.map(d => ({
                    date: d.date,
                    certified: d.certified,
                    submitted: d.submitted,
                    approved: d.approved
                }))
            })));
            setTimecards(data);
            setError(null);
            setIsOffline(false);
            setConsecutiveErrors(0);
            consecutiveErrorsRef.current = 0;
            setLastSuccessfulFetch(now);
            lastSuccessfulFetchRef.current = now;
        } catch (err) {
            console.error('Error fetching timecards:', err);
            const newErrorCount = consecutiveErrorsRef.current + 1;
            setConsecutiveErrors(newErrorCount);
            consecutiveErrorsRef.current = newErrorCount;
            
            console.log(`Consecutive errors: ${newErrorCount}`);
            
            if (err.message === 'Offline' || err.message.includes('Service temporarily unavailable')) {
                setIsOffline(true);
                setError(`Service temporarily unavailable. Retrying... (${newErrorCount} consecutive errors)`);
            } else if (err.message.includes('Only team leads can access')) {
                setError('You must be a team lead to access this page');
                setIsOffline(false);
            } else {
                setError('Failed to fetch timecards');
                setIsOffline(false);
            }
            
            // Don't clear timecards on temporary errors - keep showing last known data
            if (!err.message.includes('Only team leads can access')) {
                // Keep existing timecards visible during temporary outages
            } else {
                setTimecards([]); // Only clear for permission errors
            }
        } finally {
            setLoading(false);
        }
    }, []); // No dependencies needed with refs

    useEffect(() => {
        console.log('HoursApproval useEffect: Setting up polling, globalPollingActive:', globalPollingActive);
        
        // If global polling is already active, don't start another one
        if (globalPollingActive) {
            console.log('Global polling already active, skipping setup');
            return;
        }
        
        // Clear any existing global interval
        if (globalPollingInterval) {
            console.log('Clearing existing global interval:', globalPollingInterval);
            clearInterval(globalPollingInterval);
            globalPollingInterval = null;
        }
        
        // Mark global polling as active
        globalPollingActive = true;
        
        // Initial fetch
        fetchTimecards();
        
        // Set up auto-refresh every 30 seconds using global singleton
        globalPollingInterval = setInterval(() => {
            const now = new Date().toLocaleTimeString();
            console.log(`[${now}] Global interval triggered, checking circuit breaker...`);
            fetchTimecards();
        }, 30000);
        
        intervalRef.current = globalPollingInterval;
        console.log('Global interval set up with ID:', globalPollingInterval);
        
        return () => {
            console.log('HoursApproval cleanup: Clearing global interval', globalPollingInterval);
            if (globalPollingInterval) {
                clearInterval(globalPollingInterval);
                globalPollingInterval = null;
                globalPollingActive = false;
            }
            intervalRef.current = null;
        };
    }, []); // No dependencies to prevent recreation

    const getWeekRange = (startDate, endDate) => {
        const start = parseUTCDate(startDate);
        const end = parseUTCDate(endDate);
        const weekNum = format(start, 'w');
        return `Week ${weekNum} (${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')})`;
    };


    const handleViewTimecard = (timecard) => {
        console.log('Viewing timecard:', {
            id: timecard._id,
            user: timecard.user.name,
            status: timecard.status,
            days: timecard.days.map(d => ({
                date: d.date,
                certified: d.certified,
                submitted: d.submitted,
                approved: d.approved
            }))
        });
        setSelectedTimecard(timecard);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedTimecard(null);
        // Force a refresh when dialog is closed
        fetchTimecards();
    };

    const handleSaveApproval = async (updateData) => {
        if (!selectedTimecard) return;

        try {
            console.log('Approving timecard with days:', updateData.days);
            
            // Pass the days data to the approve endpoint
            const updatedTimecard = await approveTimecard(selectedTimecard._id, updateData.days);
            
            // Update the timecard in the list if it's not fully approved
            if (updatedTimecard.status !== 'Approved') {
                setTimecards(prev => prev.map(tc => 
                    tc._id === updatedTimecard._id ? updatedTimecard : tc
                ));
                handleDialogClose();
            } else {
                // If fully approved, refresh the list to remove it
                await fetchTimecards();
                handleDialogClose();
            }
            setError(null);
        } catch (error) {
            console.error('Error approving timecard:', error);
            setError('Failed to approve timecard');
        }
    };

    // Handle sorting
    const handleSort = (field) => {
        setSortConfig(prev => ({
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
            field
        }));
    };

    // Helper function to extract last name from full name
    const getLastName = (fullName) => {
        const nameParts = fullName.trim().split(' ');
        return nameParts[nameParts.length - 1].toLowerCase();
    };

    // Group timecards by week with auto-aging logic
    const weekData = useMemo(() => {
        if (!timecards.length) return {};
        
        const weeks = {};
        
        timecards.forEach(timecard => {
            const weekKey = getWeekRange(timecard.startDate, timecard.endDate);
            if (!weeks[weekKey]) {
                weeks[weekKey] = {
                    weekRange: weekKey,
                    timecards: [],
                    approvedUsers: new Set(),
                    totalUsers: new Set(),
                    lastApprovalTime: null
                };
            }
            
            weeks[weekKey].timecards.push(timecard);
            weeks[weekKey].totalUsers.add(timecard.user._id);
            
            if (timecard.status === 'Approved') {
                weeks[weekKey].approvedUsers.add(timecard.user._id);
                if (timecard.approvedAt) {
                    const approvalTime = new Date(timecard.approvedAt);
                    if (!weeks[weekKey].lastApprovalTime || approvalTime > weeks[weekKey].lastApprovalTime) {
                        weeks[weekKey].lastApprovalTime = approvalTime;
                    }
                }
            }
        });
        
        return weeks;
    }, [timecards]);

    // Filter out aged weeks with auto-aging logic
    const visibleWeeks = useMemo(() => {
        const now = new Date();
        return Object.entries(weekData).filter(([weekKey, data]) => {
            if (hiddenWeeks.has(weekKey)) return false;
            
            // Check if all users have approved timecards
            const allUsersApproved = data.approvedUsers.size === data.totalUsers.size && data.totalUsers.size > 0;
            
            if (allUsersApproved && data.lastApprovalTime) {
                const hoursSinceApproval = (now - data.lastApprovalTime) / (1000 * 60 * 60);
                if (hoursSinceApproval >= 12) {
                    // Schedule for hiding
                    setTimeout(() => {
                        setHiddenWeeks(prev => new Set([...prev, weekKey]));
                    }, 100);
                    return false;
                }
            }
            return true;
        }).sort(([, a], [, b]) => {
            // Sort weeks by end date (oldest first - ascending order)
            const dateA = new Date(a.timecards[0]?.endDate || 0);
            const dateB = new Date(b.timecards[0]?.endDate || 0);
            return dateA - dateB;
        });
    }, [weekData, hiddenWeeks]);

    if (loading && timecards.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1,
                paddingTop: 0,
                paddingBottom: '88px',
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5,
                paddingTop: 0,
                paddingBottom: 0.5,
            }
        }}>
            {error && (
                <Alert 
                    severity={isOffline ? "warning" : "error"} 
                    sx={{ mb: 2 }}
                >
                    {error}
                    {isOffline && consecutiveErrors >= 2 && (
                        <div style={{ marginTop: '8px', fontSize: '0.875rem' }}>
                            Polling reduced due to service unavailability. Last successful fetch: {
                                new Date(lastSuccessfulFetch).toLocaleTimeString()
                            }
                        </div>
                    )}
                </Alert>
            )}
            
            {/* Week Cards */}
            {visibleWeeks.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                        {error ? 'Access denied' : 'No timecards pending approval'}
                    </Typography>
                </Paper>
            ) : (
                visibleWeeks.map(([weekKey, weekInfo]) => (
                    <WeekCard
                        key={weekKey}
                        weekRange={weekInfo.weekRange}
                        timecards={weekInfo.timecards}
                        approvedUserCount={weekInfo.approvedUsers.size}
                        onViewTimecard={handleViewTimecard}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                ))
            )}

            <HoursApprovalDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                timecard={selectedTimecard}
                onSave={handleSaveApproval}
            />
        </Box>
    );
};

export default HoursApproval;
